import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { MediaType } from '../types';
import { ChevronLeftIcon, ChevronRightIcon, CloseIcon } from './Icons';
import { stickerTextStyle } from './StickerText';
import { CustomAudioPlayer } from './AudioComponents';

export interface MediaViewerItem {
    url: string;
    type: MediaType;
    messageId: number | string;
    mediaIndex: number;
    senderId: string;
    timestamp: string;
    caption?: string;
}

interface MediaViewerModalProps {
    isOpen: boolean;
    onClose: () => void;
    mediaList: MediaViewerItem[];
    initialIndex: number;
    anonymousAliases?: Record<string, { name: string, pfp: string }>;
}

export const MediaViewerModal: React.FC<MediaViewerModalProps> = ({
    isOpen,
    onClose,
    mediaList,
    initialIndex,
    anonymousAliases
}) => {
    const { currentUser, usersData } = useAuth();
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [touchStartX, setTouchStartX] = useState<number | null>(null);
    const [touchEndX, setTouchEndX] = useState<number | null>(null);

    // Sync state when initialIndex changes
    useEffect(() => {
        setCurrentIndex(initialIndex);
    }, [initialIndex]);

    // Keyboard controls
    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'ArrowLeft') {
                handlePrev();
            } else if (e.key === 'ArrowRight') {
                handleNext();
            } else if (e.key === 'Escape') {
                onClose();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, currentIndex, mediaList]);

    if (!isOpen || mediaList.length === 0) return null;

    const activeItem = mediaList[currentIndex];
    if (!activeItem) return null;

    const currentUserData = usersData[currentUser || ''];
    const userThemeColor = currentUserData?.appThemeColor || '#3b82f6';
    const userTextColor = currentUserData?.appTextColor || '#ffffff';
    const userOpacity = (currentUserData?.postBlurOpacity !== undefined ? currentUserData.postBlurOpacity : 50) / 100;

    const getExactModalBg = (color: string, opacityVal: number) => {
        if (!color) return `rgba(26, 26, 46, ${opacityVal})`;
        if (color.startsWith('rgba')) return color.replace(/[\d.]+\)$/, `${opacityVal})`);
        if (color.startsWith('rgb')) return color.replace('rgb', 'rgba').replace(')', `, ${opacityVal})`);
        let hex = color.replace('#', '');
        if (hex.length === 3 || hex.length === 4) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return (!isNaN(r) && !isNaN(g) && !isNaN(b)) ? `rgba(${r}, ${g}, ${b}, ${opacityVal})` : `rgba(26, 26, 46, ${opacityVal})`;
    };

    const modalBg = getExactModalBg(userThemeColor, userOpacity);

    const handlePrev = () => {
        setCurrentIndex((prev) => (prev > 0 ? prev - 1 : mediaList.length - 1));
    };

    const handleNext = () => {
        setCurrentIndex((prev) => (prev < mediaList.length - 1 ? prev + 1 : 0));
    };

    // Touch events for swiping
    const handleTouchStart = (e: React.TouchEvent) => {
        setTouchStartX(e.touches[0].clientX);
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        setTouchEndX(e.touches[0].clientX);
    };

    const handleTouchEnd = () => {
        if (touchStartX === null || touchEndX === null) return;
        const diffX = touchStartX - touchEndX;
        const threshold = 50;

        if (diffX > threshold) {
            handleNext();
        } else if (diffX < -threshold) {
            handlePrev();
        }

        setTouchStartX(null);
        setTouchEndX(null);
    };

    // Get sender details
    const alias = anonymousAliases?.[activeItem.senderId];
    const isAnonymous = !!anonymousAliases;
    const senderData = usersData[activeItem.senderId];
    const senderName = isAnonymous && alias ? alias.name : (senderData?.displayName || activeItem.senderId);
    const senderPfp = isAnonymous && alias ? alias.pfp : (senderData?.pfp || '/assets/goodieboy.png');

    const formatTimestamp = (ts: string) => {
        try {
            const date = new Date(ts);
            return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) + ' - ' + date.toLocaleDateString();
        } catch (e) {
            return '';
        }
    };

    return (
        <div
            className="fixed inset-0 flex items-center justify-center z-[100] p-4 animate-fade-in backdrop-blur-md"
            style={{ backgroundColor: modalBg }}
            onClick={onClose}
        >
            <div
                className="rounded-[2.5rem] w-full max-w-4xl overflow-hidden flex flex-col relative border border-white/10 shadow-2xl backdrop-blur-2xl"
                style={{ backgroundColor: 'rgba(10, 10, 15, 0.85)', color: userTextColor }}
                onClick={(e) => e.stopPropagation()}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                {/* Header Section */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-white/5 bg-black/20">
                    <div className="flex items-center gap-3">
                        <img
                            src={senderPfp}
                            alt=""
                            className="w-10 h-10 rounded-full object-cover border border-white/10"
                        />
                        <div className="flex flex-col">
                            <span
                                className="text-sm font-bold"
                                style={{ color: userThemeColor, ...stickerTextStyle(0.15) }}
                            >
                                {senderName}
                            </span>
                            <span className="text-[10px] text-white/50">
                                {formatTimestamp(activeItem.timestamp)}
                            </span>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        {/* Slide Counter */}
                        {mediaList.length > 1 && (
                            <span
                                className="text-xs font-bold px-3 py-1 rounded-full bg-white/5 border border-white/10 text-white/70"
                                style={stickerTextStyle(0.18)}
                            >
                                {currentIndex + 1} of {mediaList.length}
                            </span>
                        )}
                        <button
                            onClick={onClose}
                            className="p-2 rounded-full hover:bg-white/10 text-white/70 hover:text-white transition-colors border border-white/5 hover:border-white/10 active:scale-95"
                        >
                            <CloseIcon className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Media Content Area */}
                <div className="flex-grow flex items-center justify-between p-6 relative min-h-[300px] md:min-h-[450px]">
                    {/* Left Chevron */}
                    {mediaList.length > 1 && (
                        <button
                            onClick={handlePrev}
                            className="absolute left-4 z-10 p-3 rounded-full bg-black/40 hover:bg-black/60 border border-white/10 hover:border-white/20 text-white/70 hover:text-white transition-all active:scale-90 shadow-lg"
                        >
                            <ChevronLeftIcon className="w-6 h-6" />
                        </button>
                    )}

                    {/* Media Render */}
                    <div className="w-full flex justify-center items-center">
                        {activeItem.type === 'image' && (
                            <img
                                src={activeItem.url}
                                alt=""
                                className="max-w-full max-h-[55vh] object-contain rounded-2xl shadow-xl animate-scale-in"
                            />
                        )}
                        {activeItem.type === 'video' && (
                            <video
                                key={activeItem.url}
                                src={activeItem.url}
                                controls
                                autoPlay
                                playsInline
                                className="max-w-full max-h-[55vh] object-contain rounded-2xl shadow-xl"
                            />
                        )}
                        {(activeItem.type === 'audio' || activeItem.type === 'voice') && (
                            <div className="w-full max-w-md p-6 bg-black/40 border border-white/10 rounded-[2rem] shadow-xl flex flex-col items-center gap-4">
                                <div className="p-4 bg-white/5 rounded-full border border-white/5">
                                    <svg className="w-12 h-12" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.114 5.636a9 9 0 010 12.728M16.463 8.288a5.25 5.25 0 010 7.424M6.75 8.25l4.72-4.72a.75.75 0 011.28.53v15.88a.75.75 0 01-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.01 9.01 0 012.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75z" />
                                    </svg>
                                </div>
                                <div className="w-full">
                                    <CustomAudioPlayer src={activeItem.url} authorId={activeItem.senderId} />
                                </div>
                                <span className="text-[10px] text-white/40 tracking-widest uppercase font-bold">
                                    {activeItem.type} Attachment
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Right Chevron */}
                    {mediaList.length > 1 && (
                        <button
                            onClick={handleNext}
                            className="absolute right-4 z-10 p-3 rounded-full bg-black/40 hover:bg-black/60 border border-white/10 hover:border-white/20 text-white/70 hover:text-white transition-all active:scale-90 shadow-lg"
                        >
                            <ChevronRightIcon className="w-6 h-6" />
                        </button>
                    )}
                </div>

                {/* Footer Section / Caption */}
                {activeItem.caption && (
                    <div className="px-8 py-5 border-t border-white/5 bg-black/40 flex flex-col gap-1">
                        <span className="text-xs text-white/40 font-bold uppercase tracking-wider">Caption</span>
                        <p
                            className="text-sm leading-relaxed"
                            style={stickerTextStyle(0.18)}
                        >
                            {activeItem.caption}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};
